import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { useAppSelector } from '@/redux/hooks';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getMergedRangeFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import {
    modifyParamsRemovePage,
    modifyParamsRemoveParamByFacetId,
    modifyParamsWithRange,
    parseQueryParams,
} from '@/utils/queryParams';

type FormData = {
    max: number | string;
    min: number | string;
};

export const BuyersPremiumFilter = () => {
    const formatMessage = useAppFormatMessage();
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const facetId = FacetTypes.BUYERS_PREMIUM_RANGE_ID;
    const buyersPremiumFacet = useAppSelector((state) => getMergedRangeFacet(state, facetId));
    const { submitted: isLoadingSearchResults } = useAppSelector(getSearchUiState);

    const {
        formState: { errors },
        handleSubmit,
        register,
        watch,
    } = useForm<FormData>({
        defaultValues: {
            max: buyersPremiumFacet?.range?.max === 0 ? '' : (buyersPremiumFacet?.range?.max ?? ''),
            min: buyersPremiumFacet?.range?.min === 0 ? '' : (buyersPremiumFacet?.range?.min ?? ''),
        },
    });

    const formWatch = watch();

    const onBuyersPremiumRangeChanged = useCallback(
        ({ max, min }: FormData) => {
            let newParams = modifyParamsWithRange(queryParams, facetId, Number(min), Number(max));
            if (min === 0 && max === 0) {
                newParams = modifyParamsRemoveParamByFacetId(newParams, facetId);
            }

            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams({
                currentOpenMenu: CurrentOpenMenu.AllFilters,
                filterAction:
                    min === 0 && max === 0 ? FilterAction.BuyersPremiumRemoved : FilterAction.BuyersPremiumChanged,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [queryParams, facetId, navigateWithParams]
    );

    // RESET FORM WHEN INPUTS ARE EMPTY AND BUYERS PREMIUM FACET IS NOT EMPTY
    useEffect(() => {
        if (buyersPremiumFacet?.range?.max !== 0 && formWatch.max === '' && formWatch.min === '') {
            onBuyersPremiumRangeChanged({ max: 0, min: 0 });
        }
        // callback and facet are excluded from dependencies to prevent infinite loop
        // updating URL triggers server response with new facet data, which would re-run the effect indefinitely.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formWatch.max, formWatch.min]);

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={String(formatMessage(['searchFacetTitleBuyersPremium']))}
            open
        >
            <form
                className="grid grid-cols-[1fr_min-content_1fr_min-content] items-start gap-xs"
                onSubmit={handleSubmit(onBuyersPremiumRangeChanged)}
            >
                <TextInput
                    {...register('min', { required: true })}
                    disabled={isLoadingSearchResults}
                    labelText="Min"
                    placeholder="1"
                    small
                    suffix={formWatch.min ? '%' : ''}
                    type="number"
                />
                <BodyPrimary className="mt-md">-</BodyPrimary>
                <TextInput
                    {...register('max', {
                        required: true,
                        validate: (value) => Number(value) >= Number(formWatch.min),
                    })}
                    disabled={isLoadingSearchResults}
                    errorText="Please enter a valid range"
                    hasError={Boolean(errors.max)}
                    labelText="Max"
                    placeholder="100"
                    small
                    suffix={formWatch.max ? '%' : ''}
                    type="number"
                />
                <Button
                    className="mt-[18px] mr-[1px]"
                    loading={isLoadingSearchResults}
                    size="sm"
                    type="submit"
                    variant="outlined"
                >
                    {formatMessage(['searchPage', 'facets', 'applyFilter'])}
                </Button>
            </form>
        </Accordion>
    );
};
